import React, { useState, useEffect } from 'react';
import {
  BookOpen, Plus, Trash2, Loader2, Send,
  Clock, CheckCircle, XCircle, AlertCircle, Edit2,
  MessageSquare, ArrowLeft, CornerDownRight, User, Sparkles,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';
import api from '../../api';

const STATUS = {
  pending:  { label: 'Pending Review', color: 'bg-amber-100 text-amber-700',     icon: Clock },
  approved: { label: 'Approved',       color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  rejected: { label: 'Rejected',       color: 'bg-red-100 text-red-700',         icon: XCircle },
};

export default function LessonNotesPage() {
  const [notes, setNotes]             = useState([]);
  const [myClasses, setMyClasses]     = useState([]);
  const [myAssignments, setMyAssignments] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [view, setView]               = useState('list');   // list | create | edit | detail
  const [selected, setSelected]       = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [form, setForm]               = useState({ class_id: '', subject: '', topic: '', content: '', timetable_day: '', timetable_period: '', due_date: '' });
  const [saving, setSaving]           = useState(false);
  const [generating, setGenerating]   = useState(false);
  const [newComment, setNewComment]   = useState('');
  const [replyTo, setReplyTo]         = useState(null);
  const [postingComment, setPostingComment] = useState(false);

  useEffect(() => {
    loadNotes();
    api.get('/lesson-notes/my-classes').then(r => setMyClasses(r.data || [])).catch(() => {});
    api.get('/academics/my-assignments').then(r => setMyAssignments(r.data || [])).catch(() => {});
  }, []);

  const loadNotes = async () => {
    setLoading(true);
    try {
      const r = await api.get('/lesson-notes');
      setNotes(r.data || []);
    } catch { toast.error('Failed to load notes'); }
    finally { setLoading(false); }
  };

  const loadDetail = async (noteId) => {
    try {
      const r = await api.get(`/lesson-notes/${noteId}`);
      setSelected(r.data);
      setView('detail');
      // Mark comments as read
      api.put(`/lesson-notes/${noteId}/mark-read`, {}).catch(() => {});
    } catch { toast.error('Failed to load note'); }
  };

  const handleSave = async () => {
    if (!form.class_id || !form.subject.trim() || !form.topic.trim() || !form.content.trim()) {
      toast.error('All fields are required'); return;
    }
    setSaving(true);
    try {
      const timetable_slot = (form.timetable_day && form.timetable_period)
        ? { day: form.timetable_day, period: form.timetable_period }
        : null;
      const payload = { class_id: form.class_id, subject: form.subject, topic: form.topic, content: form.content, timetable_slot, due_date: form.due_date || null };
      if (view === 'edit' && selected) {
        await api.put(`/lesson-notes/${selected.id}`, payload);
        toast.success('Note updated and resubmitted for review');
      } else {
        await api.post('/lesson-notes', payload);
        toast.success('Note submitted for admin review');
      }
      setForm({ class_id: '', subject: '', topic: '', content: '', timetable_day: '', timetable_period: '', due_date: '' });
      setView('list');
      loadNotes();
    } catch (err) { toast.error(err?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const generateWithAI = async () => {
    if (!form.subject?.trim() || !form.topic?.trim()) {
      toast.error('Enter subject and topic before generating'); return;
    }
    const className = myClasses.find(c => c.id === form.class_id)?.name || form.class_id || 'Secondary School';
    const token = localStorage.getItem('eduva_token');
    setGenerating(true);
    setForm(p => ({ ...p, content: '' }));
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/lesson-note`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ subject: form.subject, class_level: className, topic: form.topic, duration: 40 }),
      });
      if (!res.ok) throw new Error('AI request failed');
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6).trim();
          if (!raw) continue;
          try {
            const parsed = JSON.parse(raw);
            if (parsed.token) setForm(p => ({ ...p, content: p.content + parsed.token }));
            if (parsed.done) toast.success('Lesson plan generated!');
            if (parsed.error) throw new Error(parsed.error);
          } catch {}
        }
      }
    } catch (err) {
      toast.error('AI generation failed — please try again');
    } finally { setGenerating(false); }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Delete this lesson note?')) return;
    try {
      await api.delete(`/lesson-notes/${noteId}`);
      toast.success('Deleted');
      if (selected?.id === noteId) { setSelected(null); setView('list'); }
      loadNotes();
    } catch (err) {
      toast.error(err?.detail || 'Cannot delete this note');
    }
  };

  const openEdit = () => {
    const slot = selected.timetable_slot || {};
    setForm({ class_id: selected.class_id, subject: selected.subject, topic: selected.topic, content: selected.content, timetable_day: slot.day || '', timetable_period: slot.period || '', due_date: selected.due_date || '' });
    setPreviewMode(false);
    setView('edit');
  };

  const handleComment = async () => {
    if (!newComment.trim()) return;
    setPostingComment(true);
    try {
      const r = await api.post(`/lesson-notes/${selected.id}/comments`, { content: newComment.trim(), parent_id: replyTo || null });
      setSelected(p => ({ ...p, comments: [...(p.comments || []), r.data] }));
      setNewComment(''); setReplyTo(null);
    } catch (err) { toast.error(err?.detail || 'Failed to post'); }
    finally { setPostingComment(false); }
  };

  const deleteComment = async (commentId) => {
    try {
      await api.delete(`/lesson-notes/${selected.id}/comments/${commentId}`);
      setSelected(p => ({ ...p, comments: (p.comments || []).filter(c => c.id !== commentId && c.parent_id !== commentId) }));
    } catch { toast.error('Failed to delete'); }
  };

  const pendingCount = notes.filter(n => n.status === 'pending').length;

  return (
    <div className="flex h-full min-h-[calc(100vh-64px)]" data-testid="lesson-notes-page">

      {/* ── Left: Notes List ─────────────────────────── */}
      <div className={`flex flex-col border-r border-slate-200 bg-white flex-shrink-0 w-full lg:w-80 xl:w-96 ${view !== 'list' ? 'hidden lg:flex' : 'flex'}`}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h1 className="text-base font-bold text-slate-900">Lesson Notes</h1>
            {pendingCount > 0 && <p className="text-xs text-amber-600 mt-0.5">{pendingCount} awaiting approval</p>}
          </div>
          <button onClick={() => { setForm({ class_id: '', subject: '', topic: '', content: '' }); setPreviewMode(false); setView('create'); }}
            className="flex items-center gap-1.5 bg-[#2563eb] hover:bg-blue-700 text-white text-xs font-bold px-3 py-2 rounded-xl"
            data-testid="new-note-btn">
            <Plus className="w-3.5 h-3.5" /> New Note
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-blue-600 animate-spin" /></div>
          ) : notes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center text-slate-400">
              <BookOpen className="w-10 h-10 mb-3 opacity-30" />
              <p className="text-sm font-semibold">No notes yet</p>
              <p className="text-xs mt-1">Click "New Note" to create your first</p>
            </div>
          ) : notes.map(note => {
            const S = STATUS[note.status] || STATUS.pending;
            return (
              <button key={note.id} onClick={() => loadDetail(note.id)}
                className={`w-full text-left px-5 py-4 border-b border-slate-50 hover:bg-slate-50 transition-colors ${selected?.id === note.id ? 'bg-blue-50 border-l-2 border-l-[#2563eb]' : ''}`}
                data-testid={`note-item-${note.id}`}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="text-sm font-bold text-slate-800 leading-tight line-clamp-2">{note.topic}</p>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${S.color}`}>{S.label}</span>
                </div>
                <p className="text-xs text-slate-500">{note.subject} · {note.class_name}</p>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xs text-slate-400">{note.created_at?.slice(0, 10)}</span>
                  {note.comment_count > 0 && (
                    <span className="flex items-center gap-1 text-xs text-slate-400">
                      <MessageSquare className="w-3 h-3" /> {note.comment_count}
                    </span>
                  )}
                </div>
                {note.reject_reason && (
                  <p className="mt-1.5 text-xs text-red-600 bg-red-50 rounded-lg px-2 py-1 line-clamp-1">
                    {note.reject_reason}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Right: Detail/Form ───────────────────────── */}
      <div className={`flex-1 flex flex-col overflow-hidden bg-slate-50 ${view === 'list' ? 'hidden lg:flex' : 'flex'}`}>

        {/* Create / Edit */}
        {(view === 'create' || view === 'edit') && (
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-3xl mx-auto px-6 py-6">
              <div className="flex items-center gap-3 mb-5">
                <button onClick={() => setView(selected ? 'detail' : 'list')}
                  className="flex items-center gap-1.5 text-slate-500 hover:text-slate-700 text-sm"
                  data-testid="cancel-form-btn">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <h2 className="text-base font-bold text-slate-900">{view === 'edit' ? 'Edit Note' : 'New Lesson Note'}</h2>
              </div>

              {/* Re-approval notice for editing approved notes */}
              {view === 'edit' && selected?.status === 'approved' && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3.5 mb-4 flex items-start gap-2.5">
                  <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800">
                    <strong>Editing an approved note</strong> will reset its status to <em>Pending Review</em> and require school admin re-approval before students can see the updated version.
                  </p>
                </div>
              )}

              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Class *</label>
                    <select value={form.class_id} onChange={e => setForm(p => ({ ...p, class_id: e.target.value, subject: '' }))}
                      className="w-full h-10 px-3 text-sm border border-slate-200 rounded-xl bg-white outline-none focus:border-blue-400"
                      data-testid="note-class-select">
                      <option value="">Select class</option>
                      {myClasses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    {myClasses.length === 0 && (
                      <p className="text-xs text-amber-600 mt-1">No assigned classes. Ask admin to assign you.</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Subject *</label>
                    {(() => {
                      const subjectsForClass = form.class_id
                        ? [...new Set(myAssignments.filter(a => a.class_id === form.class_id).map(a => a.subject_name).filter(Boolean))]
                        : [];
                      if (subjectsForClass.length > 0) {
                        return (
                          <select
                            value={form.subject}
                            onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                            className="w-full h-10 px-3 text-sm border border-slate-200 rounded-xl bg-white outline-none focus:border-blue-400"
                            data-testid="note-subject-select"
                          >
                            <option value="">Select subject</option>
                            {subjectsForClass.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        );
                      }
                      return (
                        <input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                          placeholder={form.class_id ? 'No subjects assigned for this class' : 'Select a class first'}
                          className="w-full h-10 px-3 text-sm border border-slate-200 rounded-xl outline-none focus:border-blue-400 placeholder-slate-400"
                          data-testid="note-subject-input" />
                      );
                    })()}
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Topic *</label>
                  <input value={form.topic} onChange={e => setForm(p => ({ ...p, topic: e.target.value }))}
                    placeholder="e.g. Quadratic Equations"
                    className="w-full h-10 px-3 text-sm border border-slate-200 rounded-xl outline-none focus:border-blue-400"
                    data-testid="note-topic-input" />
                </div>

                {/* Timetable slot */}
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Timetable Slot <span className="font-normal text-slate-400 normal-case">(optional)</span></label>
                  <div className="grid grid-cols-2 gap-3">
                    <select value={form.timetable_day} onChange={e => setForm(p => ({ ...p, timetable_day: e.target.value }))}
                      className="h-10 px-3 text-sm border border-slate-200 rounded-xl bg-white outline-none focus:border-blue-400"
                      data-testid="timetable-day-select">
                      <option value="">No day</option>
                      {['Monday','Tuesday','Wednesday','Thursday','Friday'].map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <select value={form.timetable_period} onChange={e => setForm(p => ({ ...p, timetable_period: e.target.value }))}
                      className="h-10 px-3 text-sm border border-slate-200 rounded-xl bg-white outline-none focus:border-blue-400"
                      data-testid="timetable-period-select">
                      <option value="">No period</option>
                      {['Period 1','Period 2','Period 3','Period 4','Period 5','Period 6','Period 7','Period 8'].map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Content (Markdown) *</label>
                    <div className="flex items-center gap-2">
                      {/* AI Generate button */}
                      <button
                        type="button"
                        onClick={generateWithAI}
                        disabled={generating || !form.subject?.trim() || !form.topic?.trim()}
                        className="flex items-center gap-1.5 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 disabled:opacity-50 text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-sm transition-all"
                        data-testid="generate-ai-btn"
                        title={!form.subject?.trim() || !form.topic?.trim() ? 'Enter subject and topic first' : 'Generate full lesson plan with AI'}
                      >
                        {generating
                          ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating...</>
                          : <><Sparkles className="w-3.5 h-3.5" /> Generate with AI</>}
                      </button>
                      <div className="flex border border-slate-200 rounded-lg overflow-hidden">
                        <button onClick={() => setPreviewMode(false)}
                          className={`px-3 py-1 text-xs font-semibold ${!previewMode ? 'bg-[#2563eb] text-white' : 'text-slate-500 hover:bg-slate-50'}`}>
                          Write
                        </button>
                        <button onClick={() => setPreviewMode(true)}
                          className={`px-3 py-1 text-xs font-semibold ${previewMode ? 'bg-[#2563eb] text-white' : 'text-slate-500 hover:bg-slate-50'}`}>
                          Preview
                        </button>
                      </div>
                    </div>
                  </div>
                  {generating && (
                    <div className="flex items-center gap-2 text-xs text-violet-600 bg-violet-50 border border-violet-100 rounded-xl px-3 py-2 mb-2">
                      <Sparkles className="w-3.5 h-3.5 animate-pulse" />
                      AI is writing your lesson plan — content will appear below as it streams...
                    </div>
                  )}
                  {previewMode ? (
                    <div className="min-h-64 p-4 border border-slate-200 rounded-xl bg-slate-50 prose prose-sm max-w-none">
                      {form.content ? <ReactMarkdown>{form.content}</ReactMarkdown> : <p className="text-slate-400 text-sm">Nothing to preview</p>}
                    </div>
                  ) : (
                    <textarea value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))}
                      rows={16}
                      placeholder={`# Lesson Title\n\n## Objectives\nBy the end, students should be able to...\n\n## Main Content\n\n## Summary\n\n## Homework`}
                      className="w-full px-3 py-3 text-sm border border-slate-200 rounded-xl outline-none focus:border-blue-400 resize-y font-mono leading-relaxed"
                      data-testid="note-content-textarea" />
                  )}
                  <p className="text-xs text-slate-400 mt-1">Supports markdown: **bold**, *italic*, ## headings, - lists</p>
                </div>

                {/* Due date */}
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Study-By Date <span className="font-normal text-slate-400 normal-case">(optional — shown as deadline to students)</span></label>
                  <input
                    type="date"
                    value={form.due_date}
                    onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))}
                    className="h-10 px-3 text-sm border border-slate-200 rounded-xl outline-none focus:border-blue-400 bg-white"
                    data-testid="due-date-input"
                  />
                </div>

                <div className="flex gap-3">
                  <button onClick={() => setView(selected ? 'detail' : 'list')}
                    className="flex-1 border border-slate-200 text-slate-600 text-sm font-semibold py-2.5 rounded-xl hover:bg-slate-50">
                    Cancel
                  </button>
                  <button onClick={handleSave} disabled={saving}
                    className="flex-1 bg-[#2563eb] hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-bold py-2.5 rounded-xl flex items-center justify-center gap-2"
                    data-testid="save-note-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {saving ? 'Submitting...' : 'Submit for Review'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Detail view */}
        {view === 'detail' && selected && (
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-3xl mx-auto px-6 py-6 space-y-5">
              <button onClick={() => { setView('list'); setSelected(null); }}
                className="flex lg:hidden items-center gap-1.5 text-slate-500 text-sm"
                data-testid="back-to-list">
                <ArrowLeft className="w-4 h-4" /> All Notes
              </button>

              {/* Header */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">{selected.topic}</h2>
                    <p className="text-sm text-slate-500 mt-0.5">{selected.subject} · {selected.class_name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{selected.created_at?.slice(0, 10)}</p>
                    {selected.timetable_slot?.day && (
                      <span className="inline-flex items-center gap-1 mt-1.5 text-xs font-semibold bg-blue-50 text-blue-600 border border-blue-100 px-2.5 py-0.5 rounded-full">
                        {selected.timetable_slot.day} · {selected.timetable_slot.period}
                      </span>
                    )}
                    {selected.due_date && (
                      <DueDateBadge dueDate={selected.due_date} className="mt-1.5" />
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {(() => { const S = STATUS[selected.status] || STATUS.pending; const Icon = S.icon; return (
                      <span className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full ${S.color}`}>
                        <Icon className="w-3.5 h-3.5" /> {S.label}
                      </span>
                    ); })()}
                    <div className="flex gap-1">
                      <button onClick={openEdit}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                        title={selected.status === 'approved' ? 'Edit — will resubmit for approval' : 'Edit'}
                        data-testid="edit-note-btn">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      {selected.status !== 'approved' && (
                        <button onClick={() => handleDelete(selected.id)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"
                          data-testid="delete-note-btn">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {selected.reject_reason && (
                  <div className="bg-red-50 border border-red-100 rounded-xl p-3 flex gap-2 mt-3">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-bold text-red-600">Rejection Reason</p>
                      <p className="text-xs text-red-700 mt-0.5">{selected.reject_reason}</p>
                      <p className="text-xs text-red-500 mt-1">Edit and resubmit for review.</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Note Content</p>
                <div className="prose prose-sm max-w-none text-slate-700">
                  <ReactMarkdown>{selected.content || ''}</ReactMarkdown>
                </div>
              </div>

              {/* Comments — teacher can reply to student comments */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="comments-section">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">
                  Student Discussion ({(selected.comments || []).length})
                </p>
                {selected.status !== 'approved' ? (
                  <p className="text-sm text-slate-400 text-center py-6">
                    Comments will be available once the admin approves this note.
                  </p>
                ) : (
                  <CommentThread
                    comments={selected.comments || []}
                    onDelete={deleteComment}
                    replyTo={replyTo}
                    setReplyTo={setReplyTo}
                    newComment={newComment}
                    setNewComment={setNewComment}
                    onSubmit={handleComment}
                    posting={postingComment}
                    currentRole="teacher"
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Empty */}
        {view === 'list' && (
          <div className="flex-1 hidden lg:flex flex-col items-center justify-center text-slate-400">
            <BookOpen className="w-12 h-12 mb-3 opacity-20" />
            <p className="text-sm font-semibold">Select a note</p>
            <p className="text-xs mt-1">or create a new one</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Due Date Badge ──────────────────────────────────────────────── */
export function DueDateBadge({ dueDate, className = '' }) {
  if (!dueDate) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  const due   = new Date(dueDate); due.setHours(0,0,0,0);
  const diff  = Math.ceil((due - today) / (1000 * 60 * 60 * 24));

  let style, label;
  if (diff < 0) {
    style = 'bg-red-100 text-red-700 border-red-200';
    label = `Overdue · ${due.toLocaleDateString('en-NG', { day: 'numeric', month: 'short' })}`;
  } else if (diff === 0) {
    style = 'bg-red-100 text-red-700 border-red-200';
    label = 'Due Today';
  } else if (diff <= 3) {
    style = 'bg-amber-100 text-amber-700 border-amber-200';
    label = `Due in ${diff} day${diff !== 1 ? 's' : ''}`;
  } else if (diff <= 7) {
    style = 'bg-yellow-50 text-yellow-700 border-yellow-200';
    label = `Study by ${due.toLocaleDateString('en-NG', { weekday: 'short', day: 'numeric', month: 'short' })}`;
  } else {
    style = 'bg-slate-100 text-slate-600 border-slate-200';
    label = `Study by ${due.toLocaleDateString('en-NG', { day: 'numeric', month: 'short' })}`;
  }

  return (
    <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full border ${style} ${className}`}
      data-testid="due-date-badge">
      {label}
    </span>
  );
}


/* ── Shared CommentThread component ─────────────────────────────── */
export function CommentThread({ comments, onDelete, replyTo, setReplyTo, newComment, setNewComment, onSubmit, posting, currentRole }) {
  const topLevel = comments.filter(c => !c.parent_id);
  const replies  = (parentId) => comments.filter(c => c.parent_id === parentId);

  const RoleBadge = ({ role }) => {
    if (role === 'teacher' || role === 'class_teacher')
      return <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">Teacher</span>;
    if (role === 'student')
      return <span className="text-[10px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">Student</span>;
    if (role === 'school_admin' || role === 'school_owner')
      return <span className="text-[10px] font-bold bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">Admin</span>;
    return null;
  };

  return (
    <div className="space-y-4">
      {topLevel.length === 0 && (
        <p className="text-sm text-slate-400 text-center py-4">No comments yet.</p>
      )}

      {topLevel.map(comment => (
        <div key={comment.id} className="space-y-2">
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
              <User className="w-4 h-4 text-slate-400" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                <p className="text-sm font-bold text-slate-800">{comment.user_name}</p>
                <RoleBadge role={comment.user_role} />
                <p className="text-xs text-slate-400">{comment.created_at?.slice(0, 16).replace('T', ' ')}</p>
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">{comment.content}</p>
              <div className="flex items-center gap-3 mt-1.5">
                {setReplyTo && (
                  <button onClick={() => setReplyTo(replyTo === comment.id ? null : comment.id)}
                    className="flex items-center gap-1 text-xs text-slate-400 hover:text-blue-600">
                    <CornerDownRight className="w-3 h-3" /> Reply
                  </button>
                )}
                {onDelete && (
                  <button onClick={() => onDelete(comment.id)}
                    className="text-xs text-slate-300 hover:text-red-500">Delete</button>
                )}
              </div>
              {replyTo === comment.id && (
                <div className="mt-2 flex gap-2">
                  <input value={newComment} onChange={e => setNewComment(e.target.value)}
                    placeholder="Write a reply..."
                    className="flex-1 h-9 px-3 text-sm border border-slate-200 rounded-xl outline-none focus:border-blue-400"
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onSubmit(); } }}
                    data-testid="reply-input" />
                  <button onClick={onSubmit} disabled={posting}
                    className="px-3 bg-[#2563eb] text-white rounded-xl text-xs font-bold hover:bg-blue-700 disabled:opacity-60"
                    data-testid="post-reply-btn">
                    {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Reply'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {replies(comment.id).map(reply => (
            <div key={reply.id} className="ml-11 flex gap-3">
              <div className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                <User className="w-3.5 h-3.5 text-blue-400" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <p className="text-sm font-bold text-slate-800">{reply.user_name}</p>
                  <RoleBadge role={reply.user_role} />
                  <p className="text-xs text-slate-400">{reply.created_at?.slice(0, 16).replace('T', ' ')}</p>
                </div>
                <p className="text-sm text-slate-700">{reply.content}</p>
                {onDelete && (
                  <button onClick={() => onDelete(reply.id)}
                    className="text-xs text-slate-300 hover:text-red-500 mt-1">Delete</button>
                )}
              </div>
            </div>
          ))}
        </div>
      ))}

      {!replyTo && onSubmit && (
        <div className="flex gap-2 pt-2 border-t border-slate-100">
          <input value={newComment} onChange={e => setNewComment(e.target.value)}
            placeholder="Add a comment..."
            className="flex-1 h-9 px-3 text-sm border border-slate-200 rounded-xl outline-none focus:border-blue-400"
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onSubmit(); } }}
            data-testid="comment-input" />
          <button onClick={onSubmit} disabled={posting}
            className="px-4 bg-[#2563eb] text-white rounded-xl text-sm font-bold hover:bg-blue-700 disabled:opacity-60 flex items-center gap-1.5"
            data-testid="post-comment-btn">
            {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          </button>
        </div>
      )}
    </div>
  );
}
